import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useAuth } from './context/AuthContext';
import { buildMonitorJson } from './utils/monitorUtils';
import MonitorFilters from './components/monitor/MonitorFilters';
import useMonitorFilters from './hooks/useMonitorFilters';

const POLL_INTERVAL_MS = 60000;
const REQUEST_TIMEOUT_MS = Math.max(1000, Number(process.env.REACT_APP_ASI_MONITOR_TIMEOUT_MS) || 12000);
const LATEST_LOG_PAGE_SIZE = 1;

const createInitialTableState = () => ({
  entries: [],
  totalCount: 0,
  pageSize: LATEST_LOG_PAGE_SIZE,
  expandedRowId: null,
  currentPage: 0,
  latestLogId: null,
  lastError: null,
  isLoading: true
});

function extractContent(json) {
  if (!json) return '';
  try {
    if (json.choices && json.choices[0]) {
      const c = (json.choices[0].message && json.choices[0].message.content) ?? json.choices[0].text;
      if (typeof c === 'string') return c;
    }
    if (json.error && typeof json.error.message === 'string') return json.error.message;
    if (json.message && typeof json.message === 'string') return json.message;
    if (json.content && typeof json.content === 'string') return json.content;
    return JSON.stringify(json).slice(0, 400);
  } catch (e) {
    return '';
  }
}

async function callMonitorProxy({ signal, webSearch = false, variant = 'mini', token } = {}) {
  const start = performance.now();
  const params = new URLSearchParams();
  if (webSearch) params.set('web_search', '1');
  if (variant) params.set('variant', variant);
  const query = params.toString() ? `?${params.toString()}` : '';
  const payload = webSearch ? { web_search: true } : {};
  if (variant) {
    payload.variant = variant;
  }
  // For the Structured Output table, send the exact structured_request payload upstream
  if (variant === 'structured') {
    payload.structured_request = {
      model: 'asi1-mini:web_search',
      messages: [
        {
          role: 'user',
          content: "Provide details about the book 'To Kill a Mockingbird'."
        }
      ],
      temperature: 0.7,
      stream: false,
      max_tokens: 1000,
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'book_info',
          strict: true,
          schema: {
            type: 'object',
            properties: {
              title: {
                type: 'string',
                description: 'Title of the book'
              },
              author: {
                type: 'string',
                description: 'Author of the book'
              },
              year_published: {
                type: 'number',
                description: 'Year the book was first published'
              }
            },
            required: ['title', 'author', 'year_published'],
            additionalProperties: false
          }
        }
      }
    };
  }
  if (variant === 'tools') {
    payload.tools_request = {
      model: 'asi1-mini',
      messages: [
        {
          role: 'user',
          content: 'Todays news from india'
        }
      ],
      tools: [
        {
          type: 'function',
          function: {
            name: 'zep_knowledge_tool',
            description: `
<description>
 A tool for accessing the most recent facts and conversation history about the community.
 Use this before using a general web search tool.
</description>
<use_cases>
    <use_case>
        Look for news and details about current or recent events.
    </use_case>
    <use_case>
        Community vibes and feelings about community activities.
    </use_case>
    <use_case>
        Help with live information from the community knowledge base.
    </use_case>
</use_cases>
<limitations>
    <description>These are the things I can't do, use a different tool if you need info on these</description>
    <limitation>General search information not related to the community</limitation>
    <limitation>Weather</limitation>
</limitations>
<parameters>
    <parameter>query (str): Query string indicating what you want to search memory for.</parameter>
</parameters>
`,
            parameters: {
              properties: {
                config: {
                  description: 'Configuration for a Runnable.',
                  properties: {
                    callbacks: {
                      anyOf: [
                        {
                          items: {},
                          type: 'array'
                        },
                        {},
                        {
                          type: 'null'
                        }
                      ]
                    },
                    configurable: {
                      type: 'object'
                    },
                    max_concurrency: {
                      anyOf: [
                        {
                          type: 'integer'
                        },
                        {
                          type: 'null'
                        }
                      ]
                    },
                    metadata: {
                      type: 'object'
                    },
                    recursion_limit: {
                      type: 'integer'
                    },
                    run_id: {
                      anyOf: [
                        {
                          format: 'uuid',
                          type: 'string'
                        },
                        {
                          type: 'null'
                        }
                      ]
                    },
                    run_name: {
                      type: 'string'
                    },
                    tags: {
                      items: {
                        type: 'string'
                      },
                      type: 'array'
                    }
                  },
                  type: 'object'
                },
                query: {
                  type: 'string'
                }
              },
              required: ['config', 'query'],
              type: 'object'
            }
          }
        },
        {
          type: 'function',
          function: {
            name: 'web_search_tool',
            description: `
<description>
    Use this tool to perform a web search optimized for comprehensive, accurate, and trusted results.
    It is particularly useful for answering questions that are not available in your memory and existing context.
</description>
<rules>
    <rule>Don't be generic, the web is a huge place.</rule>
    <rule>Include contextual details about what you want in the query.</rule>
</rules>
<use_cases>
    <use_case>"What are some upcoming events in the community?"</use_case>
    <use_case>"Find recent articles relevant to your use case."</use_case>
    <use_case>"How do I find my way to the nearest community event?"</use_case>
</use_cases>
<limitations>
    <limitation>The tool fetches web search results and may not provide structured data like communities or events directly.</limitation>
</limitations>
<parameters>
    <parameter>query (str): A search query to find information on the web.</parameter>
</parameters>
`,
            parameters: {
              properties: {
                query: {
                  default: 'What is the latest news in the Grand Rapids area?',
                  description: 'A search query to find information on the web that includes context about the query.',
                  type: 'string'
                },
                query_description: {
                  default: 'A search query to find information on the web that includes context about the query.',
                  type: 'string'
                }
              },
              type: 'object'
            }
          }
        },
        {
          type: 'function',
          function: {
            name: 'calendar_search',
            description: `
Description: Provide responses to general questions on user's calendar about user meeting, events holidays.
Use Cases:
    - Retrieve meetings for today or any specified day.
    - Schedule a meeting, including setting the time, attendees, and details.
    - Update existing meetings with new information or adjustments.
    - Delete scheduled meetings.
    - Check user availability within a specific time frame to determine if the user is free or busy.
`,
            parameters: {
              properties: {
                allow_overlapping: {
                  default: false,
                  description: 'If True, allows creation of events that overlap with existing calendar events',
                  type: 'boolean'
                },
                attendees: {
                  anyOf: [
                    {
                      items: {
                        type: 'string'
                      },
                      type: 'array'
                    },
                    {
                      type: 'null'
                    }
                  ],
                  default: null,
                  description: 'List of attendee email addresses. Optional for Create and Update operations'
                },
                description: {
                  anyOf: [
                    {
                      type: 'string'
                    },
                    {
                      type: 'null'
                    }
                  ],
                  default: null,
                  description: 'Detailed event description. Optional for Create and Update operations'
                },
                end_time: {
                  anyOf: [
                    {
                      type: 'string'
                    },
                    {
                      type: 'null'
                    }
                  ],
                  description: 'Event end time in ISO format (YYYY-MM-DDThh:mm:ss). Must be after start_time. Required for Create, Update, and Check Availability'
                },
                event_id: {
                  anyOf: [
                    {
                      type: 'string'
                    },
                    {
                      type: 'null'
                    }
                  ],
                  default: null,
                  description: 'Unique event identifier. Required for Update and Delete operations'
                },
                location: {
                  anyOf: [
                    {
                      type: 'string'
                    },
                    {
                      type: 'null'
                    }
                  ],
                  default: null,
                  description: 'Physical or virtual event location. Optional for Create and Update operations'
                },
                operation: {
                  default: 'Check Availability',
                  description: 'Calendar operation to perform. Must be one of: Create, Retrieve, Update, Delete, or Check Availability',
                  type: 'string'
                },
                recurrence: {
                  anyOf: [
                    {
                      properties: {
                        additional_dates: {
                          anyOf: [
                            {
                              items: {
                                type: 'string'
                              },
                              type: 'array'
                            },
                            {
                              type: 'null'
                            }
                          ],
                          default: null,
                          description: 'List of additional dates to include in the recurrence pattern (RDATE)',
                          title: 'Additional Dates'
                        },
                        days: {
                          anyOf: [
                            {
                              items: {
                                enum: ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'],
                                title: 'WeekdayEnum',
                                type: 'string'
                              },
                              type: 'array'
                            },
                            {
                              type: 'null'
                            }
                          ],
                          default: null,
                          description: 'List of days when the event should recur (e.g., [\'MO\', \'WE\', \'FR\'])',
                          title: 'Days'
                        },
                        exclude_dates: {
                          anyOf: [
                            {
                              items: {
                                type: 'string'
                              },
                              type: 'array'
                            },
                            {
                              type: 'null'
                            }
                          ],
                          default: null,
                          description: 'List of dates to exclude from the recurrence pattern (EXDATE)',
                          title: 'Exclude Dates'
                        },
                        frequency: {
                          enum: ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'],
                          title: 'FrequencyEnum',
                          type: 'string'
                        },
                        interval: {
                          anyOf: [
                            {
                              minimum: 1,
                              type: 'integer'
                            },
                            {
                              type: 'null'
                            }
                          ],
                          default: 1,
                          description: 'Interval between recurrences (e.g., 1 for every week, 2 for every other week)',
                          title: 'Interval'
                        },
                        until: {
                          anyOf: [
                            {
                              type: 'string'
                            },
                            {
                              type: 'null'
                            }
                          ],
                          default: null,
                          description: 'End date for the recurrence in ISO format (e.g., 2024-12-31T23:59:59)',
                          title: 'Until'
                        },
                        update_type: {
                          anyOf: [
                            {
                              enum: ['single', 'future', 'all'],
                              title: 'RecurrenceUpdateType',
                              type: 'string'
                            },
                            {
                              type: 'null'
                            }
                          ],
                          default: 'all',
                          description: 'Specifies how to update recurring events: single instance, all future events, or entire series'
                        }
                      },
                      required: ['frequency'],
                      title: 'RecurrenceConfig',
                      type: 'object'
                    },
                    {
                      type: 'null'
                    }
                  ],
                  default: null,
                  description: 'Recurrence configuration for repeating events. Optional for Create and Update operations'
                },
                start_time: {
                  anyOf: [
                    {
                      type: 'string'
                    },
                    {
                      type: 'null'
                    }
                  ],
                  description: 'Event start time in ISO format (YYYY-MM-DDThh:mm:ss). Required for Create, Update, and Check Availability'
                },
                summary: {
                  anyOf: [
                    {
                      type: 'string'
                    },
                    {
                      type: 'null'
                    }
                  ],
                  default: null,
                  description: 'Event title/summary. Required for Create, optional for Update'
                }
              },
              required: ['start_time', 'end_time'],
              type: 'object'
            }
          }
        }
      ],
      temperature: 0.1,
      stream: false,
      max_tokens: 1024
    };
  }

  const res = await fetch(`/api/asi-monitor-proxy${query}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(payload),
    signal
  });
  const clientDurationMs = Math.round(performance.now() - start);
  const statusCode = res.status;
  const ct = res.headers.get('content-type') || '';
  const upstreamHeader = res.headers.get('x-asi-upstream-duration-ms');
  const durationMs = upstreamHeader ? (parseInt(upstreamHeader, 10) || clientDurationMs) : clientDurationMs;
  let content = '';
  let data = null;

  if (ct.includes('application/json')) {
    try {
      data = await res.json();
      content = extractContent(data);
    } catch (e) {
      content = `Invalid JSON response (${e && e.message ? e.message : String(e)})`;
    }
  } else {
    try {
      content = await res.text();
    } catch (e) {
      content = `No body (${e && e.message ? e.message : String(e)})`;
    }
  }

  return { upstreamStatus: statusCode, durationMs, content, data };
}

function MonitorTable({ title, tableState, setTableState, statusResolver, moreLink }) {
  const {
    entries,
    expandedRowId,
    latestLogId,
    lastError,
    isLoading
  } = tableState;

  const hasEntries = entries.length > 0;
  const latestEntry = hasEntries ? entries[0] : null;
  const latestStatus = latestEntry ? statusResolver(latestEntry) : null;
  const isExpanded = latestEntry ? expandedRowId === latestEntry.id : false;
  const displayJson = isExpanded && latestEntry?.raw ? buildMonitorJson(latestEntry.raw, latestEntry.variant) : '';

  const toggleExpansion = (rowId) => {
    setTableState((prev) => ({
      ...prev,
      expandedRowId: prev.expandedRowId === rowId ? null : rowId
    }));
  };

  return (
    <section className="mt-6">
      <h2 className="text-lg font-semibold mb-2 text-gray-700">{title}</h2>

      {lastError && (
        <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">
          {lastError}
        </div>
      )}

      <div className="border rounded-lg bg-white shadow-sm">
        <div className="hidden md:block">
          <div className="overflow-x-auto rounded-lg" aria-busy={isLoading}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-blue-600">
                <tr>
                  <th className="px-4 py-2 text-center text-xs font-semibold text-white uppercase tracking-wider">Timestamp</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold text-white uppercase tracking-wider">Status Code</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold text-white uppercase tracking-wider">Response Time (sec)</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold text-white uppercase tracking-wider">Output</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold text-white uppercase tracking-wider">Status</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold text-white uppercase tracking-wider">Data</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
              {(!hasEntries && !isLoading) ? (
                <tr>
                  <td colSpan="6" className="px-4 py-6 text-center text-gray-500 text-sm">
                    No monitor results recorded yet.
                  </td>
                </tr>
              ) : null}
              {isLoading && !hasEntries ? (
                <tr>
                  <td colSpan="6" className="px-4 py-3 text-center text-sm text-gray-500">
                    Loading latest results...
                  </td>
                </tr>
              ) : null}
              {latestEntry ? (
                <React.Fragment key={latestEntry.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap text-center">{new Date(latestEntry.timestamp).toLocaleString()}</td>
                    <td className="px-4 py-2 text-sm text-center">
                      <span className={`px-2 py-1 rounded-md text-xs ${
                        latestEntry.status >= 200 && latestEntry.status < 300
                          ? 'bg-green-100 text-green-700'
                          : latestEntry.status >= 400
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {latestEntry.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap text-center">{(latestEntry.durationMs / 1000).toFixed(1)} sec</td>
                    <td className="px-4 py-2 text-sm text-gray-700 text-center">
                      {latestEntry.status === 200 ? (
                        <span className="text-sm text-green-600 font-medium">200 OK</span>
                      ) : latestEntry.raw ? (
                        <button
                          className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
                          onClick={() => toggleExpansion(latestEntry.id)}
                          aria-expanded={isExpanded}
                          aria-controls={`row-json-${latestEntry.id}`}
                        >
                          {isExpanded ? 'Hide JSON' : 'View JSON'}
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">No JSON</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span className="relative inline-flex h-4 w-4 items-center justify-center" title={latestStatus?.tooltip || ''}>
                        <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${latestStatus?.isHealthy ? 'bg-green-400' : 'bg-red-400'} ${latestEntry.id === latestLogId ? 'animate-ping' : ''}`} />
                        <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${latestStatus?.isHealthy ? 'bg-green-500' : 'bg-red-500'} ${latestEntry.id === latestLogId ? 'animate-pulse' : ''}`}>
                          <span className="sr-only">{latestStatus?.tooltip || ''}</span>
                        </span>
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      {moreLink ? (
                        <Link
                          to={moreLink}
                          className="inline-block text-xs px-3 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-100"
                        >
                          More
                        </Link>
                      ) : null}
                    </td>
                  </tr>
                  {isExpanded && latestEntry.raw ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-3 bg-black">
                        <div
                          id={`row-json-${latestEntry.id}`}
                          className="rounded-lg border border-slate-700 shadow-inner p-4"
                        >
                          <SyntaxHighlighter
                            language="json"
                            style={vscDarkPlus}
                            customStyle={{
                              margin: 0,
                              backgroundColor: 'transparent',
                              padding: 0,
                              fontSize: '0.75rem',
                              lineHeight: 1.5
                            }}
                            showLineNumbers
                            lineNumberStyle={{
                              color: '#64748B',
                              marginRight: '1rem'
                            }}
                            wrapLongLines
                            codeTagProps={{
                              style: {
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap'
                              }
                            }}
                            lineProps={{
                              style: {
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap'
                              }
                            }}
                          >
                            {displayJson || '{}'}
                          </SyntaxHighlighter>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              ) : null}
              </tbody>
            </table>
          </div>
        </div>

        <div className="md:hidden">
          {!hasEntries && !isLoading ? (
            <div className="px-4 py-6 text-center text-sm text-gray-500">No monitor results recorded yet.</div>
          ) : null}
          {isLoading && !hasEntries ? (
            <div className="px-4 py-3 text-center text-sm text-gray-500">Loading latest results...</div>
          ) : null}
          {latestEntry ? (
            <div className="p-4 space-y-4" aria-live="polite">
              <div className="rounded-lg border border-gray-200 shadow-sm p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-900">{new Date(latestEntry.timestamp).toLocaleString()}</span>
                  <span className={`px-2 py-1 rounded-md text-xs ${
                    latestEntry.status >= 200 && latestEntry.status < 300
                      ? 'bg-green-100 text-green-700'
                      : latestEntry.status >= 400
                      ? 'bg-red-100 text-red-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {latestEntry.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs text-gray-600">
                  <div>
                    <span className="block font-semibold text-gray-800 uppercase tracking-wide text-[11px]">Response Time</span>
                    <span className="block text-sm text-gray-900 mt-0.5">{(latestEntry.durationMs / 1000).toFixed(1)} sec</span>
                  </div>
                  <div className="flex items-center justify-end">
                    <span className="relative inline-flex h-4 w-4 items-center justify-center" title={latestStatus?.tooltip || ''}>
                      <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${latestStatus?.isHealthy ? 'bg-green-400' : 'bg-red-400'} ${latestEntry.id === latestLogId ? 'animate-ping' : ''}`} />
                      <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${latestStatus?.isHealthy ? 'bg-green-500' : 'bg-red-500'} ${latestEntry.id === latestLogId ? 'animate-pulse' : ''}`}>
                        <span className="sr-only">{latestStatus?.tooltip || ''}</span>
                      </span>
                    </span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 pt-1">
                  {latestEntry.status === 200 ? (
                    <span className="text-sm text-green-600 font-medium">200 OK</span>
                  ) : latestEntry.raw ? (
                    <button
                      className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                      onClick={() => toggleExpansion(latestEntry.id)}
                      aria-expanded={isExpanded}
                      aria-controls={`mobile-row-json-${latestEntry.id}`}
                    >
                      {isExpanded ? 'Hide JSON' : 'View JSON'}
                    </button>
                  ) : (
                    <span className="text-xs text-gray-400">No JSON</span>
                  )}
                  {moreLink ? (
                    <Link
                      to={moreLink}
                      className="inline-flex items-center text-xs px-3 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-100"
                    >
                      More
                    </Link>
                  ) : null}
                </div>

                {isExpanded && latestEntry.raw ? (
                  <div className="mt-3" id={`mobile-row-json-${latestEntry.id}`}>
                    <div className="rounded-lg border border-slate-700 bg-black/90">
                      <SyntaxHighlighter
                        language="json"
                        style={vscDarkPlus}
                        customStyle={{
                          margin: 0,
                          backgroundColor: 'transparent',
                          padding: '1rem',
                          fontSize: '0.75rem',
                          lineHeight: 1.5
                        }}
                        showLineNumbers
                        lineNumberStyle={{
                          color: '#64748B',
                          marginRight: '1rem'
                        }}
                        wrapLongLines
                        codeTagProps={{
                          style: {
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap'
                          }
                        }}
                        lineProps={{
                          style: {
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap'
                          }
                        }}
                      >
                        {displayJson || '{}'}
                      </SyntaxHighlighter>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function CombinedMonitorTable({ services }) {
  return (
    <section className="mt-6">
      <div className="border rounded-lg bg-white shadow-sm">
        <div className="hidden md:block">
          <div className="overflow-x-auto rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-blue-600">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">Service</th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider">Timestamp</th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider">Status Code</th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider">Response Time (sec)</th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider">Output</th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider">Status</th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider">Data</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {services.map((svc) => {
                  const { title, tableState, setTableState, statusResolver, moreLink } = svc;
                  const hasEntries = tableState.entries.length > 0;
                  const latestEntry = hasEntries ? tableState.entries[0] : null;
                  const latestStatus = latestEntry ? statusResolver(latestEntry) : null;
                  const isExpanded = latestEntry ? tableState.expandedRowId === latestEntry.id : false;
                  const displayJson = isExpanded && latestEntry?.raw ? buildMonitorJson(latestEntry.raw, latestEntry.variant) : '';
                  const toggleExpansion = (rowId) => {
                    setTableState((prev) => ({
                      ...prev,
                      expandedRowId: prev.expandedRowId === rowId ? null : rowId
                    }));
                  };
                  return (
                    <React.Fragment key={title}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-5 py-3 text-sm text-gray-900 whitespace-nowrap">
                          <div className="flex flex-col">
                            <span className="font-medium">{title}</span>
                            {tableState.lastError ? (
                              <span className="text-xs text-red-600 mt-1">{tableState.lastError}</span>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-5 py-3 text-sm text-gray-700 whitespace-nowrap text-center">
                          {latestEntry ? new Date(latestEntry.timestamp).toLocaleString() : '—'}
                        </td>
                        <td className="px-5 py-3 text-sm text-center">
                          {latestEntry ? (
                            <span className={`px-2 py-1 rounded-md text-xs ${
                              latestEntry.status >= 200 && latestEntry.status < 300
                                ? 'bg-green-100 text-green-700'
                                : latestEntry.status >= 400
                                ? 'bg-red-100 text-red-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {latestEntry.status}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-sm text-gray-700 whitespace-nowrap text-center">
                          {latestEntry ? `${(latestEntry.durationMs / 1000).toFixed(1)} sec` : '—'}
                        </td>
                        <td className="px-5 py-3 text-sm text-gray-700 text-center">
                          {latestEntry?.status === 200 ? (
                            <span className="text-sm text-green-600 font-medium">200 OK</span>
                          ) : latestEntry?.raw ? (
                            <button
                              className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
                              onClick={() => toggleExpansion(latestEntry.id)}
                              aria-expanded={isExpanded}
                              aria-controls={`row-json-${latestEntry?.id || title}`}
                            >
                              {isExpanded ? 'Hide JSON' : 'View JSON'}
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">{tableState.isLoading ? 'Loading…' : 'No JSON'}</span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-center">
                          <span className="relative inline-flex h-4 w-4 items-center justify-center" title={latestStatus?.tooltip || ''}>
                            <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${latestStatus?.isHealthy ? 'bg-green-400' : 'bg-red-400'} ${latestEntry && (tableState.latestLogId === latestEntry.id) ? 'animate-ping' : ''}`} />
                            <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${latestStatus?.isHealthy ? 'bg-green-500' : 'bg-red-500'} ${latestEntry && (tableState.latestLogId === latestEntry.id) ? 'animate-pulse' : ''}`}>
                              <span className="sr-only">{latestStatus?.tooltip || ''}</span>
                            </span>
                          </span>
                        </td>
                        <td className="px-5 py-3 text-center">
                          {moreLink ? (
                            <Link
                              to={moreLink}
                              className="inline-block text-xs px-3 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-100"
                            >
                              More
                            </Link>
                          ) : null}
                        </td>
                      </tr>
                      {isExpanded && latestEntry?.raw ? (
                        <tr>
                          <td colSpan="7" className="px-5 py-4 bg-black">
                            <div
                              id={`row-json-${latestEntry.id}`}
                              className="rounded-lg border border-slate-700 shadow-inner p-4"
                            >
                              <SyntaxHighlighter
                                language="json"
                                style={vscDarkPlus}
                                customStyle={{
                                  margin: 0,
                                  backgroundColor: 'transparent',
                                  padding: 0,
                                  fontSize: '0.75rem',
                                  lineHeight: 1.5
                                }}
                                showLineNumbers
                                lineNumberStyle={{
                                  color: '#64748B',
                                  marginRight: '1rem'
                                }}
                                wrapLongLines
                                codeTagProps={{
                                  style: {
                                    wordBreak: 'break-word',
                                    whiteSpace: 'pre-wrap'
                                  }
                                }}
                                lineProps={{
                                  style: {
                                    wordBreak: 'break-word',
                                    whiteSpace: 'pre-wrap'
                                  }
                                }}
                              >
                                {displayJson || '{}'}
                              </SyntaxHighlighter>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile layout */}
        <div className="md:hidden">
          {services.map((svc) => {
            const { title, tableState, setTableState, statusResolver, moreLink } = svc;
            const hasEntries = tableState.entries.length > 0;
            const latestEntry = hasEntries ? tableState.entries[0] : null;
            const latestStatus = latestEntry ? statusResolver(latestEntry) : null;
            const isExpanded = latestEntry ? tableState.expandedRowId === latestEntry.id : false;
            const displayJson = isExpanded && latestEntry?.raw ? buildMonitorJson(latestEntry.raw, latestEntry.variant) : '';
            const toggleExpansion = (rowId) => {
              setTableState((prev) => ({
                ...prev,
                expandedRowId: prev.expandedRowId === rowId ? null : rowId
              }));
            };
            return (
              <div key={title} className="p-4 space-y-4" aria-live="polite">
                <div className="rounded-lg border border-gray-200 shadow-sm p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-900">{title}</span>
                    {latestEntry ? (
                      <span className={`px-2 py-1 rounded-md text-xs ${
                        latestEntry.status >= 200 && latestEntry.status < 300
                          ? 'bg-green-100 text-green-700'
                          : latestEntry.status >= 400
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {latestEntry.status}
                      </span>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-xs text-gray-600">
                    <div>
                      <span className="block font-semibold text-gray-800 uppercase tracking-wide text-[11px]">Timestamp</span>
                      <span className="block text-sm text-gray-900 mt-0.5">{latestEntry ? new Date(latestEntry.timestamp).toLocaleString() : '—'}</span>
                    </div>
                    <div>
                      <span className="block font-semibold text-gray-800 uppercase tracking-wide text-[11px]">Response Time</span>
                      <span className="block text-sm text-gray-900 mt-0.5">{latestEntry ? `${(latestEntry.durationMs / 1000).toFixed(1)} sec` : '—'}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="relative inline-flex h-4 w-4 items-center justify-center" title={latestStatus?.tooltip || ''}>
                      <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${latestStatus?.isHealthy ? 'bg-green-400' : 'bg-red-400'} ${latestEntry && (tableState.latestLogId === latestEntry.id) ? 'animate-ping' : ''}`} />
                      <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${latestStatus?.isHealthy ? 'bg-green-500' : 'bg-red-500'} ${latestEntry && (tableState.latestLogId === latestEntry.id) ? 'animate-pulse' : ''}`}>
                        <span className="sr-only">{latestStatus?.tooltip || ''}</span>
                      </span>
                    </span>
                    <div className="flex items-center gap-2">
                      {latestEntry?.status === 200 ? (
                        <span className="text-sm text-green-600 font-medium">200 OK</span>
                      ) : latestEntry?.raw ? (
                        <button
                          className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                          onClick={() => toggleExpansion(latestEntry.id)}
                          aria-expanded={isExpanded}
                          aria-controls={`mobile-row-json-${latestEntry.id}`}
                        >
                          {isExpanded ? 'Hide JSON' : 'View JSON'}
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">{tableState.isLoading ? 'Loading…' : 'No JSON'}</span>
                      )}
                      {moreLink ? (
                        <Link
                          to={moreLink}
                          className="inline-flex items-center text-xs px-3 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-100"
                        >
                          More
                        </Link>
                      ) : null}
                    </div>
                  </div>

                  {isExpanded && latestEntry?.raw ? (
                    <div className="mt-3" id={`mobile-row-json-${latestEntry.id}`}>
                      <div className="rounded-lg border border-slate-700 bg-black/90">
                        <SyntaxHighlighter
                          language="json"
                          style={vscDarkPlus}
                          customStyle={{
                            margin: 0,
                            backgroundColor: 'transparent',
                            padding: '1rem',
                            fontSize: '0.75rem',
                            lineHeight: 1.5
                          }}
                          showLineNumbers
                          lineNumberStyle={{
                            color: '#64748B',
                            marginRight: '1rem'
                          }}
                          wrapLongLines
                          codeTagProps={{
                            style: {
                              wordBreak: 'break-word',
                              whiteSpace: 'pre-wrap'
                            }
                          }}
                          lineProps={{
                            style: {
                              wordBreak: 'break-word',
                              whiteSpace: 'pre-wrap'
                            }
                          }}
                        >
                          {displayJson || '{}'}
                        </SyntaxHighlighter>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function Monitor() {
  const { token } = useAuth();
  const [isChecking, setIsChecking] = useState(false);
  const [nextCheckIn, setNextCheckIn] = useState(POLL_INTERVAL_MS / 1000);
  const [modelTable, setModelTable] = useState(createInitialTableState);
  const [extendedTable, setExtendedTable] = useState(createInitialTableState);
  const [fastTable, setFastTable] = useState(createInitialTableState);
  const [imageTable, setImageTable] = useState(createInitialTableState);
  const [visionTable, setVisionTable] = useState(createInitialTableState);
  const [graphTable, setGraphTable] = useState(createInitialTableState);
  const [webTable, setWebTable] = useState(createInitialTableState);
  const [structuredTable, setStructuredTable] = useState(createInitialTableState);
  const [toolsTable, setToolsTable] = useState(createInitialTableState);
  const [streamingTable, setStreamingTable] = useState(createInitialTableState);
  const pollTimerRef = useRef(null);
  const tickTimerRef = useRef(null);
  const inFlightRef = useRef(false);

  // Filter state management
  const { getApiParams } = useMonitorFilters();
  const [appliedFilters, setAppliedFilters] = useState({
    statusCodes: { type: 'all', codes: [] },
    timePeriod: { type: 'all', startDate: null, endDate: null },
    service: 'all'
  });

  const loadLogs = useCallback(async ({ type, variant, setTableState, filters = appliedFilters }) => {
    if (!token) return;

    setTableState((prev) => ({
      ...prev,
      isLoading: true,
      lastError: null,
      currentPage: 0,
      expandedRowId: null,
      pageSize: LATEST_LOG_PAGE_SIZE
    }));

    try {
      // Build params with filters
      const params = new URLSearchParams({
        type,
        page: '0',
        pageSize: String(LATEST_LOG_PAGE_SIZE)
      });
      
      if (variant) {
        params.set('variant', variant);
      }
      
      // Add filter parameters
      if (filters.statusCodes.type !== 'all') {
        params.set('statusFilter', filters.statusCodes.type);
        if (filters.statusCodes.type === 'custom' && filters.statusCodes.codes.length > 0) {
          params.set('statusCodes', filters.statusCodes.codes.join(','));
        }
      }
      
      if (filters.timePeriod.type !== 'all') {
        if (filters.timePeriod.type === 'custom') {
          if (filters.timePeriod.startDate) {
            params.set('startDate', filters.timePeriod.startDate.toISOString());
          }
          if (filters.timePeriod.endDate) {
            params.set('endDate', filters.timePeriod.endDate.toISOString());
          }
        } else {
          params.set('timePeriod', filters.timePeriod.type);
        }
      }

      const res = await fetch(`/api/monitor-logs?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!res.ok) {
        throw new Error(`Failed to load logs (status ${res.status})`);
      }

      const data = await res.json();
      const entries = Array.isArray(data.logs)
        ? data.logs.map((item) => ({
            ...item,
            id: item.id || item._id || `${type}-${item.timestamp}`,
            raw: item.raw ?? null,
            variant: item.variant || variant || 'mini'
          }))
        : [];

      setTableState((prev) => ({
        ...prev,
        entries,
        totalCount: typeof data.total === 'number' ? data.total : entries.length,
        currentPage: 0,
        latestLogId: entries[0] ? entries[0].id : prev.latestLogId,
        expandedRowId: null,
        isLoading: false,
        lastError: null,
        pageSize: LATEST_LOG_PAGE_SIZE
      }));
    } catch (err) {
      const message = err && err.message ? err.message : 'Failed to load logs';
      setTableState((prev) => ({
        ...prev,
        isLoading: false,
        lastError: message
      }));
    }
  }, [token, appliedFilters]);

  const handleApplyFilters = useCallback((newFilters) => {
    setAppliedFilters(newFilters);
  }, []);

  const loadMiniLogs = useCallback(() => loadLogs({ type: 'model', variant: 'mini', setTableState: setModelTable, filters: appliedFilters }), [loadLogs, appliedFilters]);
  const loadExtendedLogs = useCallback(() => loadLogs({ type: 'model', variant: 'extended', setTableState: setExtendedTable, filters: appliedFilters }), [loadLogs, appliedFilters]);
  const loadFastLogs = useCallback(() => loadLogs({ type: 'model', variant: 'fast', setTableState: setFastTable, filters: appliedFilters }), [loadLogs, appliedFilters]);
  const loadImageLogs = useCallback(() => loadLogs({ type: 'model', variant: 'imagex', setTableState: setImageTable, filters: appliedFilters }), [loadLogs, appliedFilters]);
  const loadVisionLogs = useCallback(() => loadLogs({ type: 'model', variant: 'vision', setTableState: setVisionTable, filters: appliedFilters }), [loadLogs, appliedFilters]);
  const loadGraphLogs = useCallback(() => loadLogs({ type: 'model', variant: 'graph', setTableState: setGraphTable, filters: appliedFilters }), [loadLogs, appliedFilters]);
  const loadWebLogs = useCallback(() => loadLogs({ type: 'web', setTableState: setWebTable, filters: appliedFilters }), [loadLogs, appliedFilters]);
  const loadStructuredLogs = useCallback(() => loadLogs({ type: 'model', variant: 'structured', setTableState: setStructuredTable, filters: appliedFilters }), [loadLogs, appliedFilters]);
  const loadToolsLogs = useCallback(() => loadLogs({ type: 'model', variant: 'tools', setTableState: setToolsTable, filters: appliedFilters }), [loadLogs, appliedFilters]);
  const loadStreamingLogs = useCallback(() => loadLogs({ type: 'model', variant: 'streaming', setTableState: setStreamingTable, filters: appliedFilters }), [loadLogs, appliedFilters]);

  const runCheck = useCallback(async ({ webSearch = false, variant = 'mini', setTableState }) => {
    if (!token) return;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort('timeout'), REQUEST_TIMEOUT_MS);

    try {
      await callMonitorProxy({
        signal: controller.signal,
        webSearch,
        variant,
        token
      });
      setTableState((prev) => ({
        ...prev,
        lastError: null
      }));
    } catch (err) {
      const message = `Request failed: ${err && err.message ? err.message : String(err)}`;
      setTableState((prev) => ({
        ...prev,
        lastError: message
      }));
    } finally {
      clearTimeout(timeout);
    }
  }, [token]);

  const doCheck = useCallback(async () => {
    if (inFlightRef.current || !token) return;

    inFlightRef.current = true;
    setIsChecking(true);
    setNextCheckIn(POLL_INTERVAL_MS / 1000);

    try {
      await Promise.all([
        runCheck({ variant: 'mini', setTableState: setModelTable }),
        runCheck({ variant: 'extended', setTableState: setExtendedTable }),
        runCheck({ variant: 'fast', setTableState: setFastTable }),
        runCheck({ variant: 'imagex', setTableState: setImageTable }),
        runCheck({ variant: 'vision', setTableState: setVisionTable }),
        runCheck({ variant: 'graph', setTableState: setGraphTable }),
        runCheck({ webSearch: true, variant: 'mini', setTableState: setWebTable }),
        runCheck({ variant: 'structured', setTableState: setStructuredTable }),
        runCheck({ variant: 'tools', setTableState: setToolsTable }),
        runCheck({ variant: 'streaming', setTableState: setStreamingTable })
      ]);

      await Promise.all([
        loadMiniLogs(),
        loadExtendedLogs(),
        loadFastLogs(),
        loadImageLogs(),
        loadVisionLogs(),
        loadGraphLogs(),
        loadWebLogs(),
        loadStructuredLogs(),
        loadToolsLogs(),
        loadStreamingLogs()
      ]);
    } finally {
      setIsChecking(false);
      inFlightRef.current = false;
    }
  }, [loadExtendedLogs, loadFastLogs, loadGraphLogs, loadImageLogs, loadMiniLogs, loadVisionLogs, loadWebLogs, runCheck, token]);

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    loadMiniLogs();
    loadExtendedLogs();
    loadFastLogs();
    loadImageLogs();
    loadVisionLogs();
    loadGraphLogs();
    loadWebLogs();
    loadStructuredLogs();
    loadToolsLogs();
    loadStreamingLogs();
  }, [token, loadExtendedLogs, loadFastLogs, loadGraphLogs, loadImageLogs, loadMiniLogs, loadVisionLogs, loadWebLogs, loadStructuredLogs, loadToolsLogs, loadStreamingLogs]);

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    tickTimerRef.current = setInterval(() => {
      setNextCheckIn((s) => (s <= 1 ? POLL_INTERVAL_MS / 1000 : s - 1));
    }, 1000);

    doCheck();
    pollTimerRef.current = setInterval(() => {
      doCheck();
      setNextCheckIn(POLL_INTERVAL_MS / 1000);
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (tickTimerRef.current) clearInterval(tickTimerRef.current);
    };
  }, [doCheck, token]);

  const modelStatusResolver = useCallback((row) => {
    const isHealthy = row.status === 200;
    return {
      isHealthy,
      tooltip: isHealthy ? 'API healthy' : 'API issue'
    };
  }, []);

  const webStatusResolver = useCallback((row) => {
    const executableData = Array.isArray(row.raw?.executable_data) ? row.raw.executable_data : [];
    const hasResults = executableData.length > 0;
    return {
      isHealthy: hasResults,
      tooltip: hasResults ? 'Web search results found' : 'No web search results'
    };
  }, []);

  // Check if filters are active but all tables have no entries
  const hasActiveFilters = appliedFilters.statusCodes.type !== 'all' || appliedFilters.timePeriod.type !== 'all' || appliedFilters.service !== 'all';
  const allTablesEmpty = [modelTable, extendedTable, fastTable, imageTable, visionTable, graphTable, webTable, structuredTable, toolsTable, streamingTable]
    .every(table => table.entries.length === 0 && !table.isLoading);

  // Filter services based on service filter
  const allServices = [
    { title: 'ASI1 - Mini', tableState: modelTable, setTableState: setModelTable, statusResolver: modelStatusResolver, moreLink: '/monitor/model-history', variant: 'mini' },
    { title: 'ASI1 - Extended', tableState: extendedTable, setTableState: setExtendedTable, statusResolver: modelStatusResolver, moreLink: '/monitor/extended-history', variant: 'extended' },
    { title: 'ASI1 - Fast', tableState: fastTable, setTableState: setFastTable, statusResolver: modelStatusResolver, moreLink: '/monitor/fast-history', variant: 'fast' },
    { title: 'ASI1 - Graph', tableState: graphTable, setTableState: setGraphTable, statusResolver: modelStatusResolver, moreLink: '/monitor/graph-history', variant: 'graph' },
    { title: 'Image Engine', tableState: imageTable, setTableState: setImageTable, statusResolver: modelStatusResolver, moreLink: '/monitor/image-engine-history', variant: 'imagex' },
    { title: 'Vision Engine', tableState: visionTable, setTableState: setVisionTable, statusResolver: modelStatusResolver, moreLink: '/monitor/vision-engine-history', variant: 'vision' },
    { title: 'Web Search', tableState: webTable, setTableState: setWebTable, statusResolver: webStatusResolver, moreLink: '/monitor/web-history', variant: 'web' },
    { title: 'Structured Output', tableState: structuredTable, setTableState: setStructuredTable, statusResolver: modelStatusResolver, moreLink: '/monitor/structured-output-history', variant: 'structured' },
    { title: 'Tool Calling', tableState: toolsTable, setTableState: setToolsTable, statusResolver: modelStatusResolver, moreLink: '/monitor/tool-calling-history', variant: 'tools' },
    { title: 'Streaming', tableState: streamingTable, setTableState: setStreamingTable, statusResolver: modelStatusResolver, moreLink: '/monitor/streaming-history', variant: 'streaming' }
  ];

  // Filter services based on selected service
  const filteredServices = appliedFilters.service === 'all'
    ? allServices
    : allServices.filter(svc => svc.variant === appliedFilters.service);

  return (
    <div className="container mx-auto p-4 pt-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Realtime Updates</h1>
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <span>Next check in: {nextCheckIn}s</span>
          {isChecking && <span className="text-blue-600 font-medium">Running check...</span>}
        </div>
      </div>

      {/* Filter Component */}
      <MonitorFilters
        onApplyFilters={handleApplyFilters}
        initialFilters={appliedFilters}
        showTimePeriod={true}
        compact={true}
      />

      {/* No Results Message */}
      {hasActiveFilters && allTablesEmpty && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6 text-center">
          <p className="text-gray-700 font-medium mb-2">No results to display</p>
          <p className="text-sm text-gray-600">
            No monitor entries match your current filters. The latest checks may have different status codes.
          </p>
          <button
            onClick={() => handleApplyFilters({ statusCodes: { type: 'all', codes: [] }, timePeriod: { type: 'all', startDate: null, endDate: null } })}
            className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Clear filters to see all results
          </button>
        </div>
      )}

<CombinedMonitorTable
  services={filteredServices}
/>
    </div>
  );
}
